'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import apiClient, { extractApiError } from '@/lib/api-client';
import { getOrgId } from '@/lib/auth';
import { queryKeys } from '@/lib/query-keys';

/* ─── Types ─── */
export interface OrgRead {
  org_id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface UserMe {
  user_id: string;
  email: string;
  name?: string;
  phone?: string | null;
  last_login_at: string | null;
  orgs: Array<{ org_id: string; role: string }>;
  is_platform_admin?: boolean;
  // Timezone-SSOT A2/B2: account-global display-timezone preference. REQUIRED-
  // PRESENT and nullable on GET /auth/me — the server always emits the key
  // (null = "no preference"). Presentation-only: it controls how instants are
  // shown, never farm evidence, wall-clock conversion, or stored instants.
  display_timezone: string | null;
}

/**
 * Response from GET /orgs/{org}/members/{user}/contact.
 *
 * `telegram_chat_id` is only populated for admin/owner callers (debug);
 * other roles see `null`. UI should rely on `telegram_connected` for display.
 */
export interface MemberContactRead {
  telegram_connected: boolean;
  telegram_chat_id: number | null;
  whatsapp_number: string | null;
  notification_email: string | null;
}

/**
 * Request body for PATCH /orgs/{org}/members/{user}/contact.
 *
 * `telegram_chat_id` is intentionally omitted — it is populated only by the
 * Telegram bot webhook via deep-link linking, never by direct user input.
 */
export interface MemberContactPatch {
  whatsapp_number?: string | null;
  notification_email?: string | null;
}

/* ─── Org ─── */
export function useOrg() {
  return useQuery({
    queryKey: queryKeys.org.detail(getOrgId() ?? ''),
    queryFn: async () => {
      const orgId = getOrgId();
      if (!orgId) throw new Error('No org_id');
      const { data } = await apiClient.get<OrgRead>(`/orgs/${orgId}`);
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name?: string; settings?: Record<string, unknown> }) => {
      const orgId = getOrgId();
      const { data } = await apiClient.patch<OrgRead>(`/orgs/${orgId}`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.org.detail(getOrgId() ?? '') }),
    onError: (error: AxiosError) => {
      toast.error(extractApiError(error, 'İşlem başarısız oldu'));
    },
  });
}

/* ─── Me / Contact ─── */
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me.current,
    queryFn: async () => {
      const { data } = await apiClient.get<UserMe>('/auth/me');
      return data;
    },
    staleTime: 120_000,
  });
}

export function useMyContact(userId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.me.current, 'contact', userId],
    queryFn: async () => {
      const orgId = getOrgId();
      if (!orgId || !userId) throw new Error('No org/user');
      const { data } = await apiClient.get<MemberContactRead>(
        `/orgs/${orgId}/members/${userId}/contact`,
      );
      return data;
    },
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useUpdateMyContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      ...contact
    }: MemberContactPatch & { userId: string }) => {
      const orgId = getOrgId();
      const { data } = await apiClient.patch<MemberContactRead>(
        `/orgs/${orgId}/members/${userId}/contact`,
        contact,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.me.current }),
    onError: (error: AxiosError) => {
      toast.error(extractApiError(error, 'İşlem başarısız oldu'));
    },
  });
}
